/*
 * Native Possum — np-product-mobile.js
 * The phone product page (≤767px): peek carousel, colour swatches, lightbox with
 * zoom, size + size-guide bottom sheets, sticky Add to Bag, phone recommendations.
 * A VIEW over window.npProduct (np-product.js): it reads npProduct.data, changes the
 * selection through npProduct.setColour / setSize / add, and follows
 * 'np:product:change'. It mounts while the screen is ≤767px and unmounts (removes
 * everything it built, drops its listeners) when it isn't — so rotating a phone or
 * resizing a window can't leave a half-built page.
 * The size sheet reads the rendered size chart (snippets/np-size-guide:
 * .size-chart-note, .sc-cm/.sc-in; a row's first cell is a th). Header height comes from np-header.js
 * (--np-header-h); this file sets --np-vh.
 */

(function() {
  'use strict';

  var P = window.npProduct;
  if (!P) return;
  var D = P.data;

  // Debounce helper
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Title case helper
  function toTitleCase(str) {
    return str.split('/').map(function(part) {
      return part.replace(/\w\S*/g, function(txt) {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    }).join('/');
  }

  // Peek carousel constants (tuneable). The gap is also handed to CSS
  // (--np-peek-gap); the dimming of the side slides lives in np-product.css,
  // keyed off .np-peek-mode + .is-current, so switching mode can't leave it behind.
  var CAROUSEL_MARGIN_PCT = 5;
  var PEEK_GAP_PX = 3;

  // Swipe-to-dismiss constants
  var DISMISS_THRESHOLD_PX = 120;
  var DISMISS_SCALE_MIN = 0.85;
  var DISMISS_TRANSITION_MS = 300;

  P.watch('(max-width: 767px)', mount);

  function mount() {
    var controller = new AbortController();
    var on = { signal: controller.signal };   // every listener on something that outlives this view
    var created = [];                          // every node this view adds to the page

    // State
    let currentSlide = 0;
    let currentLightboxSlide = 0;
    let selectedColour = '';
    let selectedSize = '';
    let productImages = [];
    let hasSize = false;
    let isDragging = false;
    let swatchMap = {};

    let sizeChartData = null;
    // note text scraped from the rendered chart snippet
    let sizeChartNote = null;

    // Peek carousel state
    var peekMode = false;
    var peekMarginPx = 0;
    var peekSlideWidthPx = 0;
    var peekStepPx = 0;

    // Full-res image cache
    let fullResCache = {};

    // Zoom overlay state
    let zoomOverlay = null;
    let isZoomed = false;

    // DOM elements
    let carouselTrack, carouselDots, carouselEl, carouselEnlargeBtn;
    let lightbox, lightboxTrack, lightboxDots, lightboxCounter, lightboxHint;
    let sizeSheet, sheetOverlay;
    let guideSheet, guidePill;   // one-size items: "Size guide" pill + sheet
    let reviewsSheet, reviewsPill, reviewsBody, reviewsHome; // Reviews pill + sheet (NP Reviews)
    let activeSheet = null;      // whichever bottom sheet is open
    let stickyAddBtn;
    let sizePillSelect;


    // ============================================
    // FULL-RES URL HELPERS
    // ============================================
    function getFullResUrl(url) {
      if (!url) return url;
      var cleaned = url.replace(/[?&]width=\d+/, '');
      cleaned = cleaned.replace(/\?$/, '').replace(/&$/, '');
      return cleaned;
    }

    function preloadFullRes(index) {
      if (fullResCache[index]) return;

      var data = productImages[index];
      if (!data || !data.url) return;

      var fullUrl = getFullResUrl(data.url);
      var img = new Image();

      img.onload = function() {
        fullResCache[index] = {
          width: img.naturalWidth,
          height: img.naturalHeight,
          url: fullUrl,
          loaded: true
        };
      };

      img.src = fullUrl;
    }

    // ============================================
    // VIEWPORT HEIGHT LOCK
    // ============================================
    function setViewportHeight() {
      document.documentElement.style.setProperty('--np-vh', window.innerHeight + 'px');
    }

    // ============================================
    // INITIALIZATION
    // ============================================

    // ============================================
    // FIND LIQUID-RENDERED ELEMENTS
    // ============================================
    function initMobileElements() {
      stickyAddBtn = document.querySelector('.np-sticky-add');
      sizePillSelect = document.getElementById('npSizePillSelect');
      guidePill = document.getElementById('npSizeGuidePill');
      reviewsPill = document.getElementById('npReviewsPill');
      hasSize = D.hasSize;
      selectedColour = P.state.colour;
      selectedSize = P.state.sizeChosen ? P.state.size : '';

      if (sizePillSelect) sizePillSelect.addEventListener('click', function() { openSheet(); }, on);
      if (guidePill) guidePill.addEventListener('click', function() { openSheet(guideSheet); }, on);
      if (reviewsPill) reviewsPill.addEventListener('click', function() { openSheet(reviewsSheet); }, on);
      if (stickyAddBtn) stickyAddBtn.addEventListener('click', handleAddToBag, on);
    }

    // ============================================
    // DESCRIPTION TRUNCATION (on Liquid HTML)
    // ============================================

    // ============================================
    // EXTRACT DATA
    // ============================================
    function extractProductData() {
      productImages = D.media.map(function(m) {
        return { url: m.url, colour: m.colour, alt: m.alt || '', focalX: m.fx, focalY: m.fy };
      });
    }

    function extractSwatchMap() {
      swatchMap = D.swatches || {};
    }

    // ============================================
    // CELL TEXT READER
    // Body mode renders range values as two block spans
    // (.sc-cm over muted .sc-in). Reading textContent glued them
    // together with the source indentation between. Join them the
    // way single-value cells already read: 82-85cm (32-33")
    // ============================================
    // A measurement cell holds its visible text (aria-hidden) and a
    // .np-visually-hidden twin in words (snippets/np-size-guide). Only the
    // visible part is read here; cellSpeech reads the words.
    function cellText(cell) {
      if (!cell) return '';
      if (cell.querySelector('.np-visually-hidden')) {
        cell = cell.cloneNode(true);
        cell.querySelectorAll('.np-visually-hidden').forEach(function(el) { el.remove(); });
      }
      var cm = cell.querySelector('.sc-cm');
      var inch = cell.querySelector('.sc-in');
      if (cm && inch) {
        return cm.textContent.trim() + ' (' + inch.textContent.trim() + ')';
      }
      if (cm) return cm.textContent.trim();
      return cell.textContent.trim();
    }

    function cellSpeech(cell) {
      var words = cell ? cell.querySelector('.np-visually-hidden') : null;
      return words ? words.textContent.trim() : '';
    }

    // One measurement for the phone: { text, speech }.
    function cellValue(cell) {
      return { text: cellText(cell), speech: cellSpeech(cell) };
    }

    // Show a measurement: the text as seen, and the words a screen reader
    // says instead (so " isn't read as "quote", nor - as "minus").
    function showMeasurement(el, value) {
      el.textContent = '';
      if (!value || !value.speech) {
        el.textContent = value ? value.text : '--';
        return;
      }
      var seen = document.createElement('span');
      seen.setAttribute('aria-hidden', 'true');
      seen.textContent = value.text;
      var said = document.createElement('span');
      said.className = 'np-visually-hidden';
      said.textContent = value.speech;
      el.appendChild(seen);
      el.appendChild(said);
    }

    // ============================================
    // HEADER TOKENISER
    // Splits a column heading into whole words/codes so
    // "AU/NZ/UK" -> [AU, NZ, UK] and "Bust" -> [BUST].
    // Substring matching used to read the US inside BUST.
    // ============================================
    function headerTokens(header) {
      return String(header).toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
    }

    function headerMatchesPattern(header, pattern) {
      var tokens = headerTokens(header);
      var wanted = headerTokens(pattern);
      if (wanted.length === 0) return false;
      return wanted.every(function(token) {
        return tokens.indexOf(token) !== -1;
      });
    }

    // ============================================
    // TEXT ESCAPING
    // Note text comes from the rendered chart snippet and image alt
    // text from data-image-alt; both are inserted via innerHTML.
    // ============================================
    function escapeHtmlText(text) {
      return window.npEscapeHtml(text);
    }

    function extractSizeChartData() {
      var sizeGuideContent = document.getElementById('sizeGuideContent');
      if (!sizeGuideContent) {
        sizeChartData = null;
        sizeChartNote = null;
        return;
      }

      // Single source of truth for the sizing note — whatever the
      // chart snippet rendered. Body-measurement charts say "These are body
      // measurements"; garment charts say the opposite. Previously the
      // sheet hardcoded the garment wording for both.
      var noteEl = sizeGuideContent.querySelector('.size-chart-note');
      sizeChartNote = noteEl ? noteEl.textContent.trim() : null;

      var table = sizeGuideContent.querySelector('.size-chart-table, table');
      if (!table) {
        sizeChartData = null;
        return;
      }

      var headerCells = table.querySelectorAll('thead th');
      if (headerCells.length === 0) {
        sizeChartData = null;
        return;
      }

      var headers = [];
      headerCells.forEach(function(th) {
        headers.push(th.textContent.trim());
      });

      var firstHeaderLower = headers[0].toLowerCase();
      var isTransposedTable = firstHeaderLower === 'measurement' || firstHeaderLower === 'measurements';

      if (isTransposedTable) {
        var sizeNames = headers.slice(1);
        var measurementLabels = [];
        var sizeRows = {};

        // Collect each size's values once, keyed by the exact header name.
        // Alias keys are attached afterwards.
        var sizeValues = {};
        sizeNames.forEach(function(size) {
          sizeValues[size] = [];
        });

        var rows = table.querySelectorAll('tbody tr');
        rows.forEach(function(row) {
          var cells = row.querySelectorAll('th, td');
          if (cells.length === 0) return;

          var measurementLabel = cellText(cells[0]);
          measurementLabels.push(measurementLabel);

          sizeNames.forEach(function(size, index) {
            var value = cells[index + 1] ? cellValue(cells[index + 1]) : { text: '--', speech: '' };
            sizeValues[size].push(value);
          });
        });

        // Attach the finished list to each lookup alias — exact name,
        // uppercase, and first initial — writing each key at most once.
        // Previously the value was pushed once per alias while the aliases
        // resolved to the same key for single-letter sizes (S/M/L), so every
        // value landed in the list two or three times: the measurements card
        // showed the first measurement repeated and never reached the rest.
        sizeNames.forEach(function(size) {
          var aliases = [size, size.toUpperCase(), size.charAt(0).toUpperCase()];
          aliases.forEach(function(key) {
            if (!Object.prototype.hasOwnProperty.call(sizeRows, key)) {
              sizeRows[key] = sizeValues[size];
            }
          });
        });

        sizeChartData = {
          headers: measurementLabels,
          sizes: sizeRows,
          isInternational: false,
          isTransposed: true
        };
        return;
      }

      var internationalKeywords = ['AU', 'NZ', 'UK', 'USA', 'US', 'EU', 'FR', 'Italy', 'Japan'];
      // whole-token match. Substring matching read the "US" inside
      // "BUST", so body charts were treated as size-conversion tables and
      // lost their Waist and Hips columns.
      var isInternationalTable = headers.some(function(h) {
        var tokens = headerTokens(h);
        return internationalKeywords.some(function(keyword) {
          return tokens.indexOf(keyword.toUpperCase()) !== -1;
        });
      });

      var filteredHeaderIndices = [];
      var priorityRegions = [
        { patterns: ['AU/NZ/UK', 'NZ/AU', 'AU/NZ', 'UK'], label: null },
        { patterns: ['USA', 'US'], label: null },
        { patterns: ['FR/EU', 'EU', 'FR'], label: null }
      ];

      if (isInternationalTable) {
        filteredHeaderIndices.push(0);

        priorityRegions.forEach(function(region) {
          for (var i = 1; i < headers.length; i++) {
            if (filteredHeaderIndices.includes(i)) continue;

            var matches = region.patterns.some(function(pattern) {
              return headerMatchesPattern(headers[i], pattern);
            });

            if (matches) {
              filteredHeaderIndices.push(i);
              break;
            }
          }
        });

        filteredHeaderIndices.sort(function(a, b) { return a - b; });
      } else {
        filteredHeaderIndices = headers.map(function(_, i) { return i; });
      }

      var filteredHeaders = filteredHeaderIndices.map(function(i) { return headers[i]; });

      var rows = table.querySelectorAll('tbody tr');
      var sizeRows = {};

      rows.forEach(function(row) {
        var cells = row.querySelectorAll('th, td');
        if (cells.length === 0) return;

        var sizeName = cellText(cells[0]).replace(/[*]/g, '');

        var values = [];
        filteredHeaderIndices.forEach(function(colIndex, i) {
          if (i === 0) return;
          if (cells[colIndex]) {
            values.push(cellValue(cells[colIndex]));
          }
        });

        sizeRows[sizeName] = values;
        sizeRows[sizeName.toUpperCase()] = values;
      });

      sizeChartData = {
        headers: filteredHeaders.slice(1),
        sizes: sizeRows,
        isInternational: isInternationalTable,
        isTransposed: false
      };
    }

    // ============================================
    // CAROUSEL
    // ============================================
    function buildMobileCarousel() {
      var imageGallery = document.querySelector('.image-gallery');
      if (!imageGallery || productImages.length === 0) return;

      var carousel = document.createElement('div');
      carousel.className = 'np-mobile-carousel';
      carousel.setAttribute('role', 'region');
      carousel.setAttribute('aria-label', 'Product images');

      carouselTrack = document.createElement('div');
      carouselTrack.className = 'np-carousel-track';

      productImages.forEach(function(img, index) {
        var slide = document.createElement('div');
        slide.className = 'np-carousel-slide';
        // Shopify alt text when set, positional wording as fallback
        var slideAlt = img.alt || 'Product image ' + (index + 1) + ' of ' + productImages.length;
        slide.innerHTML = '<img src="' + img.url + '" alt="' + escapeHtmlText(slideAlt) + '" style="object-position: ' + img.focalX + '% ' + img.focalY + '%;" loading="' + (index === 0 ? 'eager' : 'lazy') + '">';
        carouselTrack.appendChild(slide);
      });

      carousel.appendChild(carouselTrack);

      if (productImages.length > 1) {
        var dotsContainer = document.createElement('div');
        dotsContainer.className = 'np-carousel-dots';
        dotsContainer.setAttribute('role', 'tablist');
        dotsContainer.setAttribute('aria-label', 'Image navigation');

        productImages.forEach(function(_, index) {
          var dot = document.createElement('button');
          dot.className = 'np-carousel-dot' + (index === 0 ? ' active' : '');
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
          dot.setAttribute('aria-label', 'View image ' + (index + 1));
          dot.addEventListener('click', function() { landOnSlide(index); });
          dotsContainer.appendChild(dot);
        });

        carousel.appendChild(dotsContainer);
        carouselDots = dotsContainer.querySelectorAll('.np-carousel-dot');
      }

      // Keyboard opener: transparent button over the images (pointer-events:
      // none, so swipes/taps still hit the track). Enter/Space click it and the
      // click bubbles to the handler below. Styles: main-product.liquid.
      carouselEnlargeBtn = document.createElement('button');
      carouselEnlargeBtn.type = 'button';
      carouselEnlargeBtn.className = 'np-carousel-enlarge';
      carouselEnlargeBtn.setAttribute('aria-label', 'Enlarge image');
      carouselEnlargeBtn.setAttribute('aria-haspopup', 'dialog');
      carousel.insertBefore(carouselEnlargeBtn, carousel.firstChild);

      carousel.addEventListener('click', function(e) {
        if (!isDragging) {
          openLightbox(currentSlide);
        }
      });

      imageGallery.appendChild(carousel);
      created.push(carousel);
      carouselEl = carousel;

      var hasMultiLine = D.colours.some(function(name) {
        return name.indexOf('/') !== -1 || name.length > 12;
      });
      if (!hasMultiLine) {
        carousel.classList.add('np-single-line-swatches');
      }

      // Detect peek vs standard mode after carousel is in the DOM
      initPeekMode(carousel, imageGallery);
    }

    // ============================================
    // PEEK MODE DETECTION + SETUP
    // ============================================
    function initPeekMode(carousel, imageGallery) {
      // Skip peek mode in phone landscape
      if (isPhoneLandscape()) {
        peekMode = false;
        carousel.classList.remove('np-peek-mode');
        imageGallery.style.setProperty('--np-carousel-pad', '28px');
        imageGallery.style.setProperty('--np-carousel-pad-right', '28px');
        return;
      }

      var containerW = carousel.offsetWidth;
      var containerH = carousel.offsetHeight;

      // All product images are 2:3 ratio — calculate how wide the image
      // would be if shown at full container height with no cropping
      var imageWidthAtFullHeight = containerH / 1.5;

      if (imageWidthAtFullHeight >= containerW) {
        // STANDARD MODE — image fills or exceeds container width
        // No peek needed, use existing cover behaviour
        peekMode = false;
        imageGallery.style.setProperty('--np-carousel-pad', '28px');
        imageGallery.style.setProperty('--np-carousel-pad-right', '28px');
        return;
      }

      // PEEK MODE — image is narrower than container, show full image + peek
      peekMode = true;
      carousel.classList.add('np-peek-mode');

      // Calculate dimensions
      peekMarginPx = Math.round(containerW * CAROUSEL_MARGIN_PCT / 100);
      peekSlideWidthPx = Math.round(imageWidthAtFullHeight);

      // Recalculate margin to centre the image:
      // containerW = marginLeft + slideWidth + gap + peekVisibleArea + marginRight(implicit)
      // But we want the image centred with equal margins, and the peek
      // occupies the right margin space. So:
      peekMarginPx = Math.round((containerW - peekSlideWidthPx) / 2);

      // Step = distance from one slide start to the next
      peekStepPx = peekSlideWidthPx + PEEK_GAP_PX;

      // Set slide widths via CSS custom property
      carousel.style.setProperty('--np-slide-width', peekSlideWidthPx + 'px');

      // Set swatch alignment — left padding matches the left edge of the image
      imageGallery.style.setProperty('--np-carousel-pad', peekMarginPx + 'px');
      imageGallery.style.setProperty('--np-carousel-pad-right', '28px');

      // Gap between slides (CSS applies it only in peek mode)
      carousel.style.setProperty('--np-peek-gap', PEEK_GAP_PX + 'px');

      // Position track so first slide has left margin
      goToSlide(0);
    }

    // ============================================
    // CURRENT SLIDE (both modes; CSS dims the others in peek mode)
    // ============================================
    function markCurrentSlide() {
      carouselTrack.querySelectorAll('.np-carousel-slide').forEach(function(slide, i) {
        slide.classList.toggle('is-current', i === currentSlide);
      });
    }

    // ============================================
    // CAROUSEL SWIPE
    // ============================================
    function initCarouselSwipe() {
      if (!carouselTrack) return;

      var startX = 0;
      var startTranslate = 0;

      carouselTrack.addEventListener('touchstart', function(e) {
        startX = e.touches[0].clientX;
        isDragging = false;
        carouselTrack.style.transition = 'none';

        if (peekMode) {
          // Read current pixel translateX
          startTranslate = getCurrentTranslatePx();
        } else {
          startTranslate = currentSlide * -100;
        }
      }, { passive: true });

      carouselTrack.addEventListener('touchmove', function(e) {
        var currentX = e.touches[0].clientX;
        var diff = currentX - startX;

        if (Math.abs(diff) > 10) {
          isDragging = true;
        }

        if (peekMode) {
          carouselTrack.style.transform = 'translateX(' + (startTranslate + diff) + 'px)';
        } else {
          var percent = (diff / carouselTrack.offsetWidth) * 100;
          carouselTrack.style.transform = 'translateX(' + (startTranslate + percent) + '%)';
        }
      }, { passive: true });

      carouselTrack.addEventListener('touchend', function() {
        carouselTrack.style.transition = 'transform 0.3s ease-out';

        if (peekMode) {
          var currentTranslatePx = getCurrentTranslatePx();
          var movedPx = currentTranslatePx - startTranslate;

          if (movedPx < -30 && currentSlide < productImages.length - 1) {
            landOnSlide(currentSlide + 1);
          } else if (movedPx > 30 && currentSlide > 0) {
            landOnSlide(currentSlide - 1);
          } else {
            goToSlide(currentSlide);
          }
        } else {
          var currentTransform = carouselTrack.style.transform;
          var match = currentTransform.match(/translateX\(([^)]+)%\)/);
          var currentPercent = match ? parseFloat(match[1]) : 0;
          var movedPercent = currentPercent - startTranslate;

          if (movedPercent < -10 && currentSlide < productImages.length - 1) {
            landOnSlide(currentSlide + 1);
          } else if (movedPercent > 10 && currentSlide > 0) {
            landOnSlide(currentSlide - 1);
          } else {
            goToSlide(currentSlide);
          }
        }

        setTimeout(function() { isDragging = false; }, 100);
      });
    }

    // Helper to read current pixel translate from transform
    function getCurrentTranslatePx() {
      var transform = carouselTrack.style.transform;
      var match = transform.match(/translateX\(([^)]+)px\)/);
      return match ? parseFloat(match[1]) : 0;
    }

    function goToSlide(index) {
      currentSlide = Math.max(0, Math.min(index, productImages.length - 1));

      if (peekMode) {
        // Pixel-based positioning
        var isLastSlide = currentSlide === productImages.length - 1;
        var containerW = carouselEl.offsetWidth;
        var translateX;

        if (isLastSlide && productImages.length > 1) {
          // Centre the last slide — equal taupe on both sides
          var lastSlideOffset = currentSlide * peekStepPx;
          var centreMargin = Math.round((containerW - peekSlideWidthPx) / 2);
          translateX = -(lastSlideOffset) + centreMargin;
        } else {
          translateX = -(currentSlide * peekStepPx) + peekMarginPx;
        }

        carouselTrack.style.transform = 'translateX(' + translateX + 'px)';
      } else {
        carouselTrack.style.transform = 'translateX(' + (currentSlide * -100) + '%)';
      }
      markCurrentSlide();

      updateCarouselDots();
    }

    // The shopper moved the carousel (swipe, dot, back from full screen): the
    // photo they land on picks its colour. Programmatic moves use goToSlide.
    function landOnSlide(index) {
      goToSlide(index);
      P.mediaChosen(currentSlide);
    }

    function updateCarouselDots() {
      if (!carouselDots) return;
      carouselDots.forEach(function(dot, i) {
        dot.classList.toggle('active', i === currentSlide);
        dot.setAttribute('aria-selected', i === currentSlide ? 'true' : 'false');
      });
    }

    // ============================================
    // SWATCHES SECTION
    // ============================================
    function buildMobileSwatchesSection() {
      var imageGallery = document.querySelector('.image-gallery');
      if (!imageGallery || D.colours.length === 0) return;

      var swatchSection = document.createElement('div');
      swatchSection.className = 'np-mobile-swatches-section';

      var swatchRow = document.createElement('div');
      swatchRow.className = 'np-mobile-swatches';
      swatchRow.setAttribute('role', 'listbox');
      swatchRow.setAttribute('aria-label', 'Select colour');

      D.colours.forEach(function(colour) {
        var isActive = colour === selectedColour;

        var wrapper = document.createElement('div');
        wrapper.className = 'np-mobile-swatch-wrapper' + (isActive ? ' active' : '');
        wrapper.dataset.colour = colour;

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'np-mobile-swatch' + (isActive ? ' active' : '');
        btn.setAttribute('data-np-swatch', '');
        btn.setAttribute('role', 'option');
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        btn.setAttribute('aria-label', toTitleCase(colour));
        btn.dataset.colour = colour;

        var swatchUrl = swatchMap[colour];
        if (swatchUrl) {
          var img = document.createElement('img');
          img.src = swatchUrl;
          img.alt = toTitleCase(colour);
          img.width = 44;
          img.height = 44;
          btn.appendChild(img);
        } else if (D.dots && D.dots[colour]) {
          btn.setAttribute('style', D.dots[colour]); // no photo yet: its dot
        }

        btn.addEventListener('click', function() { P.setColour(colour, { source: 'phone' }); });
        wrapper.appendChild(btn);

        // A "/" in a two-tone name starts a new line
        var label = document.createElement('span');
        label.className = 'np-mobile-colour-label';
        toTitleCase(colour).split('/').forEach(function(part, i) {
          if (i > 0) label.appendChild(document.createElement('br'));
          label.appendChild(document.createTextNode(part));
        });
        wrapper.appendChild(label);

        swatchRow.appendChild(wrapper);
      });

      swatchSection.appendChild(swatchRow);
      imageGallery.appendChild(swatchSection);
      created.push(swatchSection);
    }

    // Colour picked anywhere (a swatch here, a desktop button before a rotate, an old ?colour= link)
    function onColourChange(source) {
      selectedColour = P.state.colour;
      var activeWrapper = null;
      document.querySelectorAll('.np-mobile-swatch-wrapper').forEach(function(w) {
        var isOn = w.dataset.colour === selectedColour;
        w.classList.toggle('active', isOn);
        var b = w.querySelector('.np-mobile-swatch');
        if (b) {
          b.classList.toggle('active', isOn);
          b.setAttribute('aria-selected', isOn ? 'true' : 'false');
        }
        if (isOn) activeWrapper = w;
      });
      if (source === 'media') return;
      if (activeWrapper) activeWrapper.scrollIntoView({ behavior: window.npMotion.scroll(), inline: 'center', block: 'nearest' });
      var imageIndex = P.colourMediaIndex(selectedColour);
      if (imageIndex >= 0) goToSlide(imageIndex);
    }

    // Size picked (here or on the page) or cleared after an add
    function onSizeChange() {
      selectedSize = P.state.sizeChosen ? P.state.size : '';
      updateSizePillText();
      updateStickyBarText();
      if (!selectedSize) resetSizeSheet();
    }

    // ============================================
    // STICKY BAR TEXT UPDATES
    // ============================================
    function updateStickyBarText() {
      if (!stickyAddBtn) return;
      if (hasSize && selectedSize) {
        stickyAddBtn.textContent = 'Add to Bag \u2013 Size ' + selectedSize + P.sizePriceText(selectedSize);
      } else {
        stickyAddBtn.textContent = 'Add to Bag';
      }
    }

    function updateSizePillText() {
      if (!sizePillSelect) return;
      var pillText = sizePillSelect.querySelector('.pill-text');
      if (pillText) {
        if (selectedSize) {
          pillText.textContent = 'Size: ' + selectedSize;
          pillText.classList.add('selected');
        } else {
          pillText.textContent = 'Select Size';
          pillText.classList.remove('selected');
        }
      }
    }

    function handleAddToBag() {
      if (hasSize && !P.state.sizeChosen) {
        openSheet();
        return;
      }
      P.add({ trigger: stickyAddBtn });
    }

    // ============================================
    // RECOMMENDATIONS
    // ============================================
    function buildMobileRecommendations() {
      var section = document.querySelector('.similar-items-section');
      if (!section) return;

      var desktopGrid = section.querySelector('.recommendations-grid');
      if (!desktopGrid) return;

      var cards = desktopGrid.querySelectorAll('.product-card');
      if (cards.length === 0) return;

      var mobileRecs = document.createElement('div');
      mobileRecs.className = 'np-mobile-recommendations';

      cards.forEach(function(card) {
        var url = card.href;
        var img = card.querySelector('.product-card-image');
        var title = card.querySelector('.product-card-title');
        var price = card.querySelector('.product-card-price');

        var mobileCard = document.createElement('a');
        mobileCard.className = 'np-rec-card';
        mobileCard.href = url;

        mobileCard.innerHTML = '<div class="np-rec-image">'
          + (img ? '<img src="' + escapeHtmlText(img.src) + '" alt="' + escapeHtmlText(img.alt || '') + '" loading="lazy">' : '')
          + '</div>'
          + '<div class="np-rec-title">' + escapeHtmlText(title ? title.textContent : '') + '</div>'
          + '<div class="np-rec-price">' + escapeHtmlText(price ? price.textContent : '') + '</div>';

        mobileRecs.appendChild(mobileCard);
      });

      section.appendChild(mobileRecs);
      created.push(mobileRecs);
    }

    // ============================================
    // LIGHTBOX
    // ============================================
    function buildLightbox() {
      if (productImages.length === 0) return;

      lightbox = document.createElement('div');
      lightbox.className = 'np-lightbox';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-label', 'Image lightbox');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.tabIndex = -1;
      lightbox.inert = true; // npLayer lifts this while open

      lightbox.innerHTML = '<div class="np-lightbox-header">'
        + '<button class="np-lightbox-close" aria-label="Close lightbox">\u00d7</button>'
        + '<span class="np-lightbox-counter">1 / ' + productImages.length + '</span>'
        + '</div>'
        + '<div class="np-lightbox-content">'
        + '<div class="np-lightbox-track"></div>'
        + '</div>'
        + '<div class="np-lightbox-hint">Double-tap to zoom</div>'
        + '<div class="np-lightbox-dots"></div>';

      lightboxTrack = lightbox.querySelector('.np-lightbox-track');
      productImages.forEach(function(img, index) {
        var slide = document.createElement('div');
        slide.className = 'np-lightbox-slide';
        // Shopify alt text when set, positional wording as fallback
        var slideAlt = img.alt || 'Product image ' + (index + 1);
        slide.innerHTML = '<img class="np-lightbox-image" src="' + img.url + '" alt="' + escapeHtmlText(slideAlt) + '" style="object-position: ' + img.focalX + '% ' + img.focalY + '%;" loading="lazy">';
        lightboxTrack.appendChild(slide);
      });

      var dotsContainer = lightbox.querySelector('.np-lightbox-dots');
      productImages.forEach(function(_, index) {
        var dot = document.createElement('button');
        dot.className = 'np-lightbox-dot' + (index === 0 ? ' active' : '');
        dot.setAttribute('aria-label', 'View image ' + (index + 1));
        if (index === 0) dot.setAttribute('aria-current', 'true');
        dot.addEventListener('click', function() { goToLightboxSlide(index); });
        dotsContainer.appendChild(dot);
      });

      lightboxDots = dotsContainer.querySelectorAll('.np-lightbox-dot');
      lightboxCounter = lightbox.querySelector('.np-lightbox-counter');
      lightboxHint = lightbox.querySelector('.np-lightbox-hint');

      lightbox.querySelector('.np-lightbox-close').addEventListener('click', closeLightbox);

      document.body.appendChild(lightbox);
      created.push(lightbox);
    }

    // Reset lightbox dismiss drag styles (spring-back)
    function resetLightboxDismissStyles() {
      if (!lightbox) return;
      lightbox.style.transition = 'opacity ' + DISMISS_TRANSITION_MS + 'ms ease-out, transform ' + DISMISS_TRANSITION_MS + 'ms ease-out';
      lightbox.style.opacity = '';
      lightbox.style.transform = '';
      setTimeout(function() {
        lightbox.style.transition = '';
      }, DISMISS_TRANSITION_MS);
    }

    // ============================================
    // ZOOM OVERLAY — FRESH DIV APPROACH
    // ============================================
    // Where the photo is drawn inside its box: the whole box for cover, the
    // centred photo for contain (desktop zoomed into the phone layout).
    function photoRect(img) {
      var r = img.getBoundingClientRect();
      if (getComputedStyle(img).objectFit !== 'contain' || !img.naturalWidth) return r;
      var scale = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
      var w = img.naturalWidth * scale;
      var h = img.naturalHeight * scale;
      return { left: r.left + (r.width - w) / 2, top: r.top + (r.height - h) / 2, width: w, height: h };
    }

    function createZoomOverlay(tapX, tapY) {
      var cached = fullResCache[currentLightboxSlide];
      if (!cached || !cached.loaded) return;

      var fullW = cached.width;
      var fullH = cached.height;

      /* Get the current lightbox image to calculate tap fraction */
      var currentImg = lightboxTrack.querySelectorAll('.np-lightbox-image')[currentLightboxSlide];
      if (!currentImg) return;

      var imgRect = photoRect(currentImg);

      /* Skip if no meaningful zoom */
      if (fullW <= imgRect.width * 1.05 && fullH <= imgRect.height * 1.05) return;

      var fracX = Math.min(1, Math.max(0, (tapX - imgRect.left) / imgRect.width));
      var fracY = Math.min(1, Math.max(0, (tapY - imgRect.top) / imgRect.height));

      /* Create fresh overlay */
      var viewW = window.innerWidth;
      var viewH = window.innerHeight;

      /* Pad so image edge stops at screen edge (can't scroll past) */
      var padRight = Math.max(0, viewW - fullW);
      var padBottom = Math.max(0, viewH - fullH);

      zoomOverlay = document.createElement('div');
      zoomOverlay.setAttribute('role', 'dialog');
      zoomOverlay.setAttribute('aria-modal', 'true');
      zoomOverlay.setAttribute('aria-label', 'Zoomed image');
      zoomOverlay.tabIndex = -1;
      zoomOverlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;overflow:auto;z-index:10001;touch-action:pan-x pan-y;-webkit-overflow-scrolling:touch;scrollbar-width:none;-ms-overflow-style:none;background:#000;overscroll-behavior:contain;';
      var zoomImg = document.createElement('img');
      zoomImg.src = cached.url;
      zoomImg.style.cssText = 'display:block;width:' + fullW + 'px;height:' + fullH + 'px;user-select:none;-webkit-user-drag:none;margin:0 ' + padRight + 'px ' + padBottom + 'px 0;';
      zoomImg.alt = currentImg.alt;
      zoomImg.setAttribute('draggable', 'false');

      /* Close button on zoom overlay */
      var zoomClose = document.createElement('button');
      zoomClose.style.cssText = 'position:fixed;top:16px;right:16px;width:44px;height:44px;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.3);border:none;border-radius:50%;cursor:pointer;color:#fff;font-size:24px;line-height:1;z-index:10002;';
      zoomClose.setAttribute('aria-label', 'Close lightbox');
      zoomClose.textContent = '\u00d7';
      zoomClose.addEventListener('click', function(e) {
        e.stopPropagation();
        closeLightbox();
      });

      zoomOverlay.appendChild(zoomImg);
      zoomOverlay.appendChild(zoomClose);
      document.body.appendChild(zoomOverlay);

      /* Scroll so tapped point stays under finger */
      zoomOverlay.scrollLeft = (fracX * fullW) - tapX;
      zoomOverlay.scrollTop = (fracY * fullH) - tapY;

      isZoomed = true;

      /* Hide lightbox UI when zoomed */
      lightbox.classList.add('np-lightbox-zoomed');

      /* Stacks over the lightbox layer: Escape zooms out, focus goes back to the lightbox */
      if (window.npLayer) {
        window.npLayer.open(zoomOverlay, {
          trigger: lightbox,
          initialFocus: zoomOverlay,
          onRequestClose: destroyZoomOverlay
        });
      }

      /* Zoom dismiss state — dismiss-layer extraction approach */
      var zoomDismissLayer = null;
      var zoomDismissActive = false;
      var zoomDismissStartY = 0;
      var zoomDismissDeltaY = 0;
      var zoomCanDismiss = false; /* set on touchstart if near top edge */

      /* Tap to zoom out state */
      var zoomTapStartX = 0;
      var zoomTapStartY = 0;
      var zoomTapTime = 0;
      var zoomTapMoved = false;

      zoomOverlay.addEventListener('touchstart', function(e) {
        e.stopPropagation();
        if (e.touches.length === 1) {
          zoomTapStartX = e.touches[0].clientX;
          zoomTapStartY = e.touches[0].clientY;
          zoomTapTime = Date.now();
          zoomTapMoved = false;

          // Snapshot scroll position on touch start for dismiss eligibility
          zoomCanDismiss = zoomOverlay.scrollTop <= 5;
          zoomDismissStartY = e.touches[0].clientY;
          zoomDismissActive = false;
          zoomDismissDeltaY = 0;
        }
      }, { passive: true });

      zoomOverlay.addEventListener('touchmove', function(e) {
        e.stopPropagation();
        if (e.touches.length !== 1) return;

        var dx = e.touches[0].clientX - zoomTapStartX;
        var dy = e.touches[0].clientY - zoomTapStartY;
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
          zoomTapMoved = true;
        }

        /* If dismiss layer is active, drive the animation */
        if (zoomDismissActive && zoomDismissLayer) {
          e.preventDefault();
          zoomDismissDeltaY = Math.max(0, e.touches[0].clientY - zoomDismissStartY);
          var progress = Math.min(zoomDismissDeltaY / DISMISS_THRESHOLD_PX, 1);
          var scale = 1 - (progress * (1 - DISMISS_SCALE_MIN));
          var opacity = 1 - (progress * 0.6);
          zoomDismissLayer.style.transform = 'translateY(' + zoomDismissDeltaY + 'px) scale(' + scale + ')';
          zoomDismissLayer.style.opacity = String(opacity);
          return;
        }

        /* Detect dismiss start — at top edge, vertical-dominant downward */
        if (!zoomDismissActive && zoomCanDismiss && dy > 10 && Math.abs(dy) > Math.abs(dx)) {
          zoomDismissActive = true;

          // Freeze the scroll container
          zoomOverlay.style.overflow = 'hidden';
          zoomOverlay.style.touchAction = 'none';

          // Snapshot the image's current visual position
          var zoomImgEl = zoomOverlay.querySelector('img');
          if (zoomImgEl) {
            var imgRect = zoomImgEl.getBoundingClientRect();

            // Create dismiss layer — just the image, outside the scroll container
            zoomDismissLayer = document.createElement('div');
            zoomDismissLayer.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:10003;background:#000;will-change:transform,opacity;transform-origin:center top;';

            var dismissImg = document.createElement('img');
            dismissImg.src = zoomImgEl.src;
            dismissImg.alt = zoomImgEl.alt;
            dismissImg.setAttribute('draggable', 'false');
            dismissImg.style.cssText = 'position:absolute;top:' + imgRect.top + 'px;left:' + imgRect.left + 'px;width:' + imgRect.width + 'px;height:' + imgRect.height + 'px;user-select:none;-webkit-user-drag:none;';

            zoomDismissLayer.appendChild(dismissImg);
            document.body.appendChild(zoomDismissLayer);

            // Hide the scroll container visually (keep in DOM for cleanup)
            zoomOverlay.style.visibility = 'hidden';
          }

          e.preventDefault();
          return;
        }

        /* Native scroll handles normal panning */
      }, { passive: false });

      zoomOverlay.addEventListener('touchend', function(e) {
        e.stopPropagation();

        // Handle dismiss layer completion
        if (zoomDismissActive && zoomDismissLayer) {
          zoomDismissActive = false;

          if (zoomDismissDeltaY >= DISMISS_THRESHOLD_PX) {
            // Commit — animate out then close everything
            var exitY = zoomDismissDeltaY + 100;
            zoomDismissLayer.style.transition = 'opacity ' + DISMISS_TRANSITION_MS + 'ms ease-out, transform ' + DISMISS_TRANSITION_MS + 'ms ease-out';
            zoomDismissLayer.style.opacity = '0';
            zoomDismissLayer.style.transform = 'translateY(' + exitY + 'px) scale(' + DISMISS_SCALE_MIN + ')';

            setTimeout(function() {
              // Clean up dismiss layer
              if (zoomDismissLayer && zoomDismissLayer.parentNode) {
                zoomDismissLayer.parentNode.removeChild(zoomDismissLayer);
              }
              zoomDismissLayer = null;
              closeLightbox();
            }, DISMISS_TRANSITION_MS);
          } else {
            // Spring back — animate dismiss layer to origin, then restore scroll
            zoomDismissLayer.style.transition = 'opacity ' + DISMISS_TRANSITION_MS + 'ms ease-out, transform ' + DISMISS_TRANSITION_MS + 'ms ease-out';
            zoomDismissLayer.style.transform = 'translateY(0) scale(1)';
            zoomDismissLayer.style.opacity = '1';

            setTimeout(function() {
              // Remove dismiss layer and restore scroll container
              if (zoomDismissLayer && zoomDismissLayer.parentNode) {
                zoomDismissLayer.parentNode.removeChild(zoomDismissLayer);
              }
              zoomDismissLayer = null;
              zoomOverlay.style.overflow = 'auto';
              zoomOverlay.style.touchAction = 'pan-x pan-y';
              zoomOverlay.style.visibility = '';
            }, DISMISS_TRANSITION_MS);
          }
          return;
        }

        // Existing: tap to zoom out
        if (e.changedTouches.length === 1 && !zoomTapMoved) {
          var elapsed = Date.now() - zoomTapTime;
          if (elapsed < 300) {
            destroyZoomOverlay();
          }
        }
      }, { passive: true });
    }

    function destroyZoomOverlay() {
      if (zoomOverlay) {
        if (window.npLayer) window.npLayer.close(zoomOverlay);
        document.body.removeChild(zoomOverlay);
        zoomOverlay = null;
      }
      isZoomed = false;
      lightbox.classList.remove('np-lightbox-zoomed');
    }

    // ============================================
    // LIGHTBOX INTERACTIONS — SEPARATED DISMISS + SWIPE + ZOOM
    // ============================================
    function initLightboxInteractions() {
      if (!lightboxTrack) return;

      // ------------------------------------------
      // A) DISMISS LISTENER — on lightbox element
      //    Handles swipe-down-to-close only.
      //    Uses { passive: false } so browser can't
      //    claim the gesture before we decide direction.
      // ------------------------------------------
      var dismissState = 'idle'; // idle | pending | active | claimed-by-track
      var dismissStartX = 0;
      var dismissStartY = 0;
      var dismissDeltaY = 0;

      lightbox.addEventListener('touchstart', function(e) {
        if (isZoomed) return;
        if (e.touches.length !== 1) return;

        dismissState = 'pending';
        dismissStartX = e.touches[0].clientX;
        dismissStartY = e.touches[0].clientY;
        dismissDeltaY = 0;
      }, { passive: false });

      lightbox.addEventListener('touchmove', function(e) {
        if (isZoomed) return;
        if (dismissState === 'idle' || dismissState === 'claimed-by-track') return;
        if (e.touches.length !== 1) return;

        var diffX = e.touches[0].clientX - dismissStartX;
        var diffY = e.touches[0].clientY - dismissStartY;

        // Decide direction on first significant movement
        if (dismissState === 'pending' && (Math.abs(diffX) > 10 || Math.abs(diffY) > 10)) {
          if (diffY > 0 && Math.abs(diffY) > Math.abs(diffX)) {
            // Vertical-dominant downward — claim for dismiss
            dismissState = 'active';
            e.preventDefault();
          } else {
            // Horizontal or upward — let track handle it
            dismissState = 'claimed-by-track';
            return;
          }
        }

        if (dismissState === 'active') {
          e.preventDefault();
          dismissDeltaY = Math.max(0, e.touches[0].clientY - dismissStartY);
          var progress = Math.min(dismissDeltaY / DISMISS_THRESHOLD_PX, 1);
          var scale = 1 - (progress * (1 - DISMISS_SCALE_MIN));
          var opacity = 1 - (progress * 0.6);
          lightbox.style.opacity = String(opacity);
          lightbox.style.transform = 'translateY(' + dismissDeltaY + 'px) scale(' + scale + ')';
        }
      }, { passive: false });

      lightbox.addEventListener('touchend', function(e) {
        if (isZoomed) return;

        var prevState = dismissState;
        dismissState = 'idle';

        if (prevState === 'active') {
          if (dismissDeltaY >= DISMISS_THRESHOLD_PX) {
            // Instant close — the drag itself was the animation.
            // Animated fade looked ghostly (revealed page underneath).
            closeLightbox();
          } else {
            resetLightboxDismissStyles();
          }
        }
      }, { passive: true });

      // ------------------------------------------
      // B) TRACK LISTENERS — horizontal swipe + double-tap only
      //    No dismiss logic here. Track events fire after
      //    lightbox events (bubbling), so if dismiss claims
      //    the gesture, track sees dismissState !== 'claimed-by-track'
      //    and stays idle.
      // ------------------------------------------
      var startX = 0;
      var startTranslate = 0;
      var hasSwiped = false;
      var trackActive = false;

      /* Double-tap state */
      var dtLastTime = 0;
      var dtLastX = 0;
      var dtLastY = 0;
      var dtStartX = 0;
      var dtStartY = 0;
      var dtMoved = false;

      lightboxTrack.addEventListener('touchstart', function(e) {
        if (isZoomed) return;
        hasSwiped = false;
        trackActive = false;

        if (e.touches.length === 1) {
          startX = e.touches[0].clientX;
          startTranslate = currentLightboxSlide * -100;
          lightboxTrack.style.transition = 'none';

          /* Double-tap tracking */
          dtStartX = e.touches[0].clientX;
          dtStartY = e.touches[0].clientY;
          dtMoved = false;
        }
      }, { passive: true });

      lightboxTrack.addEventListener('touchmove', function(e) {
        if (isZoomed) return;
        if (e.touches.length !== 1) return;

        /* If dismiss claimed the gesture, do nothing */
        if (dismissState === 'active') return;

        var diffX = e.touches[0].clientX - startX;

        /* Double-tap movement tracking */
        var dtDx = e.touches[0].clientX - dtStartX;
        var dtDy = e.touches[0].clientY - dtStartY;
        if (Math.abs(dtDx) > 10 || Math.abs(dtDy) > 10) dtMoved = true;

        /* Only start horizontal swiping once dismiss has yielded */
        if (!trackActive && dismissState === 'claimed-by-track' && Math.abs(diffX) > 10) {
          trackActive = true;
        }

        if (trackActive) {
          hasSwiped = true;
          var percent = (diffX / lightboxTrack.offsetWidth) * 100;
          lightboxTrack.style.transform = 'translateX(' + (startTranslate + percent) + '%)';
        }
      }, { passive: true });

      lightboxTrack.addEventListener('touchend', function(e) {
        if (isZoomed) return;

        /* Handle horizontal swipe completion */
        if (trackActive) {
          trackActive = false;
          lightboxTrack.style.transition = 'transform 0.3s ease-out';
          var currentTransform = lightboxTrack.style.transform;
          var match = currentTransform.match(/translateX\(([^)]+)%\)/);
          var currentPercent = match ? parseFloat(match[1]) : 0;
          var movedPercent = currentPercent - startTranslate;

          if (movedPercent < -10 && currentLightboxSlide < productImages.length - 1) {
            goToLightboxSlide(currentLightboxSlide + 1);
          } else if (movedPercent > 10 && currentLightboxSlide > 0) {
            goToLightboxSlide(currentLightboxSlide - 1);
          } else if (hasSwiped) {
            goToLightboxSlide(currentLightboxSlide);
          }
        }

        /* Double-tap to zoom */
        if (e.changedTouches.length === 1 && !dtMoved) {
          var now = Date.now();
          var tapX = e.changedTouches[0].clientX;
          var tapY = e.changedTouches[0].clientY;
          var tapDistance = Math.hypot(tapX - dtLastX, tapY - dtLastY);

          if (now - dtLastTime < 400 && tapDistance < 50) {
            if (fullResCache[currentLightboxSlide] && fullResCache[currentLightboxSlide].loaded) {
              createZoomOverlay(tapX, tapY);
            }
            dtLastTime = 0;
          } else {
            dtLastTime = now;
          }
          dtLastX = tapX;
          dtLastY = tapY;
        }
      });

      // ------------------------------------------
      // C) KEYBOARD (Escape is handled centrally by npLayer)
      // ------------------------------------------
      lightbox.addEventListener('keydown', function(e) {
        if (e.key === 'ArrowLeft' && !isZoomed) goToLightboxSlide(currentLightboxSlide - 1);
        else if (e.key === 'ArrowRight' && !isZoomed) goToLightboxSlide(currentLightboxSlide + 1);
      });
    }

    function openLightbox(slideIndex) {
      if (slideIndex === undefined) slideIndex = 0;
      currentLightboxSlide = slideIndex;
      goToLightboxSlide(slideIndex);
      lightbox.classList.add('active');
      document.body.classList.add('np-lightbox-open');
      // Shared layer helper: inert page, focus, Escape, focus return
      if (window.npLayer) {
        window.npLayer.open(lightbox, {
          trigger: carouselEnlargeBtn,
          initialFocus: lightbox,
          onRequestClose: closeLightbox
        });
      } else {
        lightbox.inert = false;
        lightbox.focus();
      }

      // Preload full-res for current + adjacent slides
      preloadFullRes(slideIndex);
      if (slideIndex > 0) preloadFullRes(slideIndex - 1);
      if (slideIndex < productImages.length - 1) preloadFullRes(slideIndex + 1);

      if (lightboxHint) window.npMessage.dwell(lightboxHint, function() { lightboxHint.classList.add('hidden'); });
    }

    function closeLightbox() {
      if (isZoomed) destroyZoomOverlay();

      if (currentLightboxSlide !== currentSlide && carouselTrack) {
        carouselTrack.style.transition = 'none';
        landOnSlide(currentLightboxSlide);
        requestAnimationFrame(function() {
          requestAnimationFrame(function() {
            carouselTrack.style.transition = 'transform 0.3s ease-out';
          });
        });
      }

      lightbox.classList.remove('active');
      document.body.classList.remove('np-lightbox-open');
      if (lightboxHint) lightboxHint.classList.remove('hidden');

      // Ensure dismiss styles are fully cleared on close
      lightbox.style.opacity = '';
      lightbox.style.transform = '';
      lightbox.style.transition = '';

      if (window.npLayer) window.npLayer.close(lightbox);
    }

    function goToLightboxSlide(index) {
      currentLightboxSlide = Math.max(0, Math.min(index, productImages.length - 1));
      lightboxTrack.style.transform = 'translateX(' + (currentLightboxSlide * -100) + '%)';

      lightboxDots.forEach(function(dot, i) {
        dot.classList.toggle('active', i === currentLightboxSlide);
        if (i === currentLightboxSlide) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      lightboxCounter.textContent = (currentLightboxSlide + 1) + ' / ' + productImages.length;

      // Preload full-res for current + adjacent slides
      preloadFullRes(currentLightboxSlide);
      if (currentLightboxSlide > 0) preloadFullRes(currentLightboxSlide - 1);
      if (currentLightboxSlide < productImages.length - 1) preloadFullRes(currentLightboxSlide + 1);
    }

    // ============================================
    // BOTTOM SHEETS
    // ============================================
    function buildBottomSheets() {
      sheetOverlay = document.createElement('div');
      sheetOverlay.className = 'np-sheet-overlay';
      document.body.appendChild(sheetOverlay);
      created.push(sheetOverlay);

      sizeSheet = buildSizeSelectSheet();
      document.body.appendChild(sizeSheet);
      created.push(sizeSheet);

      if (guidePill) {
        guideSheet = buildGuideSheet();
        if (guideSheet) {
          document.body.appendChild(guideSheet);
          created.push(guideSheet);
        }
      }

      if (reviewsPill) {
        reviewsSheet = buildReviewsSheet();
        if (reviewsSheet) {
          document.body.appendChild(reviewsSheet);
          created.push(reviewsSheet);
        }
      }
    }

    // Reviews (snippets/np-reviews): the section's body is MOVED into the
    // sheet, not copied, so the reviews app's script (bound to its
    // [data-rv-root]) keeps working, and there is only ever one of it.
    // restoreReviews() puts it back when this view unmounts (rotate, resize).
    function buildReviewsSheet() {
      reviewsHome = document.getElementById('npReviews');
      reviewsBody = reviewsHome && reviewsHome.querySelector('[data-np-reviews-body]');
      if (!reviewsBody) return null;

      var sheet = document.createElement('div');
      sheet.className = 'np-bottom-sheet np-reviews-sheet';
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-label', 'Reviews');
      sheet.setAttribute('aria-modal', 'true');
      sheet.inert = true; // npLayer lifts this while open

      sheet.innerHTML = '<div class="np-sheet-handle"></div>'
        + '<div class="np-sheet-header">'
        + '<span class="np-sheet-title">Reviews</span>'
        + '<button class="np-sheet-header-btn close" aria-label="Close">\u00d7</button>'
        + '</div>'
        + '<div class="np-sheet-body"></div>';
      var body = sheet.querySelector('.np-sheet-body');
      var note = reviewsHome.querySelector('.rv-note');
      if (note) body.appendChild(note.cloneNode(true));
      body.appendChild(reviewsBody);

      sheet.querySelector('.np-sheet-header-btn.close').addEventListener('click', closeSheet);
      return sheet;
    }

    function restoreReviews() {
      if (reviewsBody && reviewsHome && reviewsBody.parentNode !== reviewsHome) reviewsHome.appendChild(reviewsBody);
    }

    // One-size items (scarves, throws): the size guide in a bottom sheet,
    // built from the guide the page already rendered (snippets/np-size-guide).
    // A single row of measurements reads as the same cream card as the
    // Select Size sheet; anything bigger keeps its table.
    function buildGuideSheet() {
      var source = document.querySelector('#sizeGuideContent .size-guide-wrapper');
      if (!source) return null;

      var sheet = document.createElement('div');
      sheet.className = 'np-bottom-sheet np-size-sheet np-guide-sheet';
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-label', 'Size guide');
      sheet.setAttribute('aria-modal', 'true');
      sheet.inert = true; // npLayer lifts this while open

      sheet.innerHTML = '<div class="np-sheet-handle"></div>'
        + '<div class="np-sheet-header">'
        + '<span class="np-sheet-title">Size guide</span>'
        + '<button class="np-sheet-header-btn close" aria-label="Close">\u00d7</button>'
        + '</div>'
        + '<div class="np-sheet-body"></div>';
      var body = sheet.querySelector('.np-sheet-body');

      var table = source.querySelector('.size-chart-table');
      var heads = table ? table.querySelectorAll('thead th') : [];
      var rows = table ? table.querySelectorAll('tbody tr') : [];
      if (table && rows.length === 1) {
        var cells = rows[0].querySelectorAll('th, td');
        var card = document.createElement('div');
        card.className = 'np-measurements-card';
        heads.forEach(function(th, i) {
          // First column is the size ("One size") — the card shows the measurements
          if (i === 0 && /^size$/i.test(th.textContent.trim())) return;
          if (!cells[i]) return;
          var row = document.createElement('div');
          row.className = 'np-measurement-row';
          row.innerHTML = '<span class="np-measurement-label"></span><span class="np-measurement-value"></span>';
          row.firstChild.textContent = th.textContent.trim();
          var shown = cells[i].cloneNode(true);
          shown.querySelectorAll('.np-visually-hidden').forEach(function(el) { el.remove(); });
          showMeasurement(row.lastChild, { text: shown.textContent.replace(/\s+/g, ' ').trim(), speech: cellSpeech(cells[i]) });
          card.appendChild(row);
        });
        body.appendChild(card);
      } else if (table) {
        body.appendChild(table.closest('.size-chart-scroll').cloneNode(true));
      }

      source.querySelectorAll('.size-chart-note').forEach(function(note) {
        var p = document.createElement('p');
        p.className = 'np-sizing-note';
        p.textContent = note.textContent.trim();
        body.appendChild(p);
      });

      // Throws: the bed guide as a short list rather than the desktop drawing
      // (np-bed-guide.js, which may load before or after this file).
      var bed = source.querySelector('.np-bed-guide');
      if (bed) {
        var addBedList = function() {
          var bedList = window.npBedGuide && window.npBedGuide.list(bed);
          if (bedList) body.appendChild(bedList);
        };
        if (window.npBedGuide) addBedList();
        else document.addEventListener('DOMContentLoaded', addBedList);
      }

      sheet.querySelector('.np-sheet-header-btn.close').addEventListener('click', closeSheet);
      return sheet;
    }

    function buildSizeSelectSheet() {
      var sheet = document.createElement('div');
      sheet.className = 'np-bottom-sheet np-size-sheet';
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-label', 'Select size');
      sheet.setAttribute('aria-modal', 'true');
      sheet.inert = true; // npLayer lifts this while open


      var pillsHTML = '';
      D.sizes.forEach(function(size) {
        var safe = escapeHtmlText(size);
        var priced = P.sizePriceText(size);   // ' · $185' when sizes are priced differently
        pillsHTML += '<button type="button" class="np-size-pill" aria-pressed="false" data-size="' + safe + '">' + safe
          + (priced ? '<span class="np-size-price">' + escapeHtmlText(priced) + '</span>' : '') + '</button>';
      });

      var measurementsHTML = '';
      var sizingNoteHTML = '';

      if (sizeChartData && sizeChartData.headers.length > 0) {
        measurementsHTML = '<div class="np-measurements-card">';
        measurementsHTML += '<p class="np-measurements-prompt">Tap a size to view measurements</p>';
        sizeChartData.headers.forEach(function(header) {
          measurementsHTML += '<div class="np-measurement-row" style="display:none;">';
          measurementsHTML += '<span class="np-measurement-label">' + header + '</span>';
          measurementsHTML += '<span class="np-measurement-value" data-measurement="' + header + '">--</span>';
          measurementsHTML += '</div>';
        });
        measurementsHTML += '</div>';

        if (sizeChartNote) {
          // Use the note the chart snippet actually rendered — body
          // charts say "These are body measurements", garment charts say the
          // opposite. Single source of truth, so new chart types are correct
          // without touching this file.
          sizingNoteHTML = '<p class="np-sizing-note">' + escapeHtmlText(sizeChartNote) + '</p>';
        } else if (sizeChartData.isTransposed) {
          sizingNoteHTML = '<p class="np-sizing-note">Length is measured from the cuff to the tip of the middle finger. Width is measured across the palm at its widest point. Gloves are designed to stretch for a comfortable fit \u2014 measurements are approximate.</p>';
        } else if (sizeChartData.isInternational) {
          sizingNoteHTML = '<p class="np-sizing-note">Size conversions are approximate and may vary between styles.</p>';
        } else {
          sizingNoteHTML = '<p class="np-sizing-note">These are garment measurements, not body measurements. Sizes are approximate and may vary by \u00b12cm due to the handcrafted nature of knitted garments.</p>';
        }
      } else {
        measurementsHTML = '<div class="np-measurements-card hidden"></div>';
      }

      sheet.innerHTML = '<div class="np-sheet-handle"></div>'
        + '<div class="np-sheet-header">'
        + '<span class="np-sheet-title">Select Size</span>'
        + '<button class="np-sheet-header-btn close" aria-label="Close">\u00d7</button>'
        + '</div>'
        + '<div class="np-sheet-body">'
        + '<div class="np-size-selector-pills">' + pillsHTML + '</div>'
        + measurementsHTML
        + sizingNoteHTML
        + '</div>'
        + '<div class="np-sheet-footer">'
        + '<button class="np-sheet-footer-btn" disabled>Add to Bag</button>'
        + '</div>';

      sheet.querySelectorAll('.np-size-pill').forEach(function(pill) {
        pill.addEventListener('click', function() { selectSheetSize(pill, sheet); });
      });

      sheet.querySelector('.np-sheet-header-btn.close').addEventListener('click', closeSheet);

      sheet.querySelector('.np-sheet-footer-btn').addEventListener('click', function() {
        if (selectedSize) {
          closeSheet();
          handleAddToBag();
        }
      });

      return sheet;
    }

    function selectSheetSize(pill, sheet) {
      var size = pill.dataset.size;

      sheet.querySelectorAll('.np-size-pill').forEach(function(p) {
        p.classList.toggle('active', p.dataset.size === size);
        p.setAttribute('aria-pressed', p.dataset.size === size ? 'true' : 'false');
      });

      var footerBtn = sheet.querySelector('.np-sheet-footer-btn');
      footerBtn.disabled = false;
      footerBtn.textContent = 'Add to Bag \u2013 Size ' + size + P.sizePriceText(size);

      updateMeasurementsCard(size, sheet);
      P.setSize(size, { source: 'phone' });   // pill + sticky text follow (onSizeChange)
    }

    function updateMeasurementsCard(size, sheet) {
      if (!sizeChartData || !sizeChartData.headers.length) return;

      var measurementsCard = sheet.querySelector('.np-measurements-card');
      if (!measurementsCard) return;

      var sizeData = sizeChartData.sizes[size];

      if (!sizeData) {
        sizeData = sizeChartData.sizes[size.toUpperCase()];
      }

      if (!sizeData) {
        var sizeUpper = size.toUpperCase();
        for (var key of Object.keys(sizeChartData.sizes)) {
          if (key.toUpperCase().startsWith(sizeUpper) || sizeUpper.startsWith(key.toUpperCase())) {
            sizeData = sizeChartData.sizes[key];
            break;
          }
        }
      }

      var prompt = measurementsCard.querySelector('.np-measurements-prompt');
      if (prompt) prompt.style.display = 'none';
      var rows = measurementsCard.querySelectorAll('.np-measurement-row');
      rows.forEach(function(row) { row.style.display = ''; });

      var valueElements = measurementsCard.querySelectorAll('.np-measurement-value');
      valueElements.forEach(function(el, index) {
        showMeasurement(el, sizeData && sizeData[index]);
      });
    }

    function openSheet(sheet) {
      sheet = sheet || sizeSheet;
      if (!sheet) return;
      if (activeSheet) return;
      activeSheet = sheet;

      sheetOverlay.classList.add('active');
      sheet.classList.add('active');
      document.body.classList.add('np-sheet-open');

      // Shared layer helper: inert page, focus, Escape, focus return.
      // Trigger is whatever opened it (size pill or sticky Add to Bag);
      // the overlay is the layer's backdrop (a tap closes it).
      var opener = document.activeElement;
      if (!opener || opener === document.body) opener = sheet === sizeSheet ? sizePillSelect : sheet === reviewsSheet ? reviewsPill : guidePill;
      if (window.npLayer) {
        window.npLayer.open(sheet, {
          trigger: opener,
          backdrop: sheetOverlay,
          initialFocus: sheet === sizeSheet ? '.np-size-pill' : '.np-sheet-header-btn.close',
          lockScroll: true,
          onRequestClose: closeSheet
        });
      }
    }

    function closeSheet() {
      // Bail out unless the sheet is actually open, so the scroll restore
      // below can't jump the page when nothing was open.
      var sheet = activeSheet;
      if (!sheet || !sheet.classList.contains('active')) return;
      activeSheet = null;

      sheet.classList.remove('active');
      sheetOverlay.classList.remove('active');
      document.body.classList.remove('np-sheet-open');

      // Scroll unlocks with the layer (npLayer lockScroll)
      if (window.npLayer) window.npLayer.close(sheet);
    }

    function resetSizeSheet() {
      if (!sizeSheet) return;
      sizeSheet.querySelectorAll('.np-size-pill').forEach(function(p) {
        p.classList.remove('active');
        p.setAttribute('aria-pressed', 'false');
      });
      var footerBtn = sizeSheet.querySelector('.np-sheet-footer-btn');
      if (footerBtn) {
        footerBtn.disabled = true;
        footerBtn.textContent = 'Add to Bag';
      }

      // Put the measurements card back to its prompt. It kept showing the
      // previous size's rows after add-to-bag cleared the selection.
      var measurementsCard = sizeSheet.querySelector('.np-measurements-card');
      if (measurementsCard) {
        var prompt = measurementsCard.querySelector('.np-measurements-prompt');
        if (prompt) prompt.style.display = '';
        measurementsCard.querySelectorAll('.np-measurement-row').forEach(function(row) {
          row.style.display = 'none';
        });
        measurementsCard.querySelectorAll('.np-measurement-value').forEach(function(el) {
          el.textContent = '--';
        });
      }
    }

    // ============================================
    // LANDSCAPE DETECTION + ORIENTATION CHANGE
    // ============================================
    // Touch screens only: a desktop browser zoomed into the phone layout is
    // short and wide too, but keeps peek mode so the whole photo shows
    // (matches the landscape block in np-product.css).
    function isPhoneLandscape() {
      return window.innerHeight <= 500 && window.matchMedia('(orientation: landscape) and (pointer: coarse)').matches;
    }

    function handleOrientationChange() {
      if (!carouselEl) return;
      var imageGallery = document.querySelector('.image-gallery');
      if (!imageGallery) return;

      // Reset carousel to standard mode
      carouselEl.classList.remove('np-peek-mode');
      peekMode = false;

      // Re-measure viewport height
      setViewportHeight();

      // Re-run peek detection with new dimensions
      requestAnimationFrame(function() {
        initPeekMode(carouselEl, imageGallery);
        goToSlide(currentSlide);
      });
    }


    // ============================================
    // ROTATION / RESIZE: re-measure the peek carousel
    // ============================================
    var remeasure = debounce(handleOrientationChange, 300);
    if (window.screen && window.screen.orientation) {
      window.screen.orientation.addEventListener('change', remeasure, on);
    }
    window.addEventListener('resize', remeasure, on);
    // Bars stuck / unstuck on a short zoomed screen change the carousel height (np-site.js)
    document.addEventListener('np:bars-change', remeasure, on);

    // ============================================
    // Keep the sticky bar above Safari's bottom toolbar
    // ============================================
    var stickyBar = document.querySelector('.np-sticky-bar');
    if (window.visualViewport && stickyBar) {
      var updateOffset = function() {
        var vv = window.visualViewport;
        var offset = window.innerHeight - (vv.offsetTop + vv.height);
        stickyBar.style.setProperty('--np-toolbar-offset', Math.max(0, offset) + 'px');
      };
      window.visualViewport.addEventListener('resize', updateOffset, on);
      window.visualViewport.addEventListener('scroll', updateOffset, on);
      updateOffset();
    }

    // ============================================
    // BUILD
    // ============================================
    setViewportHeight();
    extractProductData();
    extractSwatchMap();
    extractSizeChartData();
    initMobileElements();
    P.truncateDescription(140, 60);

    buildMobileCarousel();
    buildMobileSwatchesSection();
    buildMobileRecommendations();
    buildLightbox();
    buildBottomSheets();

    initCarouselSwipe();
    initLightboxInteractions();

    updateCarouselDots();
    updateSizePillText();
    updateStickyBarText();

    document.addEventListener('np:product:change', function(e) {
      if (e.detail.changed.indexOf('colour') !== -1) onColourChange(e.detail.source);
      if (e.detail.changed.indexOf('size') !== -1) onSizeChange();
    }, on);

    // A colour picked before this view mounted (rotate) still shows its photo
    var startIndex = P.colourMediaIndex(selectedColour);
    if (startIndex > 0 && selectedColour !== D.colours[0]) goToSlide(startIndex);

    // ============================================
    // UNMOUNT
    // ============================================
    return function unmount() {
      if (activeSheet) closeSheet();
      if (lightbox && lightbox.classList.contains('active')) closeLightbox();
      controller.abort();
      restoreReviews();
      created.forEach(function(node) { if (node.parentNode) node.parentNode.removeChild(node); });
      var imageGallery = document.querySelector('.image-gallery');
      if (imageGallery) {
        imageGallery.style.removeProperty('--np-carousel-pad');
        imageGallery.style.removeProperty('--np-carousel-pad-right');
      }
      if (stickyBar) stickyBar.style.removeProperty('--np-toolbar-offset');
      document.documentElement.style.removeProperty('--np-vh');
      document.body.classList.remove('np-sheet-open', 'np-lightbox-open');
    };
  }
})();
